import {
  Button,
  DatePicker,
  Descriptions,
  Divider,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  RcFile,
  Space,
  TextField,
  Upload,
  UploadFile,
  message,
} from '@pankod/refine-antd'
import React, { useCallback, useState } from 'react'
import { useCustomMutation, useDelete } from '@pankod/refine-core'
import dayjs from 'dayjs'
import 'dayjs/locale/id'
import locale from 'antd/lib/locale/id_ID'

import { PopDeleteConfirm } from '@resources/lms-management/components/PopDeleteConfirm'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import separateThousand from 'utils/separateThousand'

const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
  })

export const useUploadPaymentProofModal = () => {
  const [showId, setShowId] = useState<{
    show: boolean
    periodeId: string | number
    kelasId: string | number
    name: string
  }>({
    periodeId: '',
    kelasId: '',
    name: '',
    show: false,
  })

  const onClose = useCallback(() => {
    setShowId((state) => ({
      ...state,
      show: false,
    }))
  }, [])

  return {
    show: ({
      periodeId,
      name,
      kelasId,
    }: {
      periodeId: string | number
      kelasId: string | number
      name: string
    }) => {
      setShowId({ periodeId, name, show: true, kelasId })
    },
    visible: !!showId.show,
    periodeId: showId.periodeId,
    name: showId.name,
    onClose,
    kelasId: showId.kelasId,
  }
}

type UploadPaymentProofProps = {
  visible: boolean
  name: string
  kelasId: string | number
  userId: string | number
  periodeId?: string | number
  imgUrl?: string | null
  buktiBayarId?: string | null
  date?: string | null
  amount?: string | null
  notes?: string | null
  onSuccess: (id: string | number) => void
  onClose: () => void
}

const UploadPaymentProofModal = ({
  visible,
  imgUrl,
  name,
  userId,
  onSuccess,
  onClose,
  amount,
  date,
  kelasId,
  notes,
  periodeId,
  buktiBayarId,
}: UploadPaymentProofProps) => {
  const [form] = Form.useForm()
  const { mutateAsync: doDelete } = useDelete()
  const { mutateAsync: doSave } = useCustomMutation()
  const [isEditing, setEditing] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)
  const [file, setFile] = useState<UploadFile>()
  const beforeUpload = async (file: RcFile) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
    if (!isJpgOrPng) {
      message.error('Hanya dapat mengunggah berkas JPG/PNG')
    }
    const isFileSizeAcccepted = file.size / 1024 / 1024 < 16
    if (!isFileSizeAcccepted) {
      message.error('Ukuran maksimal gambar 16MB')
    }
    setFile(file)
    form.setFieldsValue({ paymentProof: file })
    setPreview(await getBase64(file))
    return false
  }

  const handleClose = () => {
    setEditing(false)
    setPreview(null)
    setFile(undefined)
    onClose()
  }

  const handleSave = async () => {
    await form.validateFields()
    const data = form.getFieldsValue()
    const formData = new FormData()
    formData.append('paymentProof', file as RcFile)
    formData.append('paymentDate', dayjs(data.paymentDate).format('YYYY-MM-DD'))
    formData.append('amount', data.amount)
    formData.append('notes', data.notes || null)
    formData.append('periodeId', String(periodeId))
    return doSave(
      {
        url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/kelas/${kelasId}/timespends/${userId}/payment-proof`,
        method: 'post',
        values: formData,
        config: {
          headers: {
            'content-type': 'multipart/form-data',
          },
        },
        successNotification: {
          message: 'Bukti pembayaran berhasil diunggah',
          type: 'success',
          description: 'Sukses',
        },
        errorNotification: (error: any) =>
          showErrorNotification(
            error,
            'There was a problem when uploading payment proof',
          ),
      },
      {
        onSuccess: () => {
          handleClose()
          onSuccess(userId)
        },
      },
    )
  }

  return (
    <Modal
      visible={visible}
      title="Bukti Pembayaran"
      onCancel={handleClose}
      footer={
        <Space>
          <Button
            onClick={
              isEditing
                ? () => {
                    setEditing(false)
                    setPreview(null)
                    setFile(undefined)
                    form.resetFields()
                  }
                : handleClose
            }
          >
            {isEditing ? 'Batal' : 'Kembali'}
          </Button>
          <Divider type="vertical" />
          {imgUrl && (
            <PopDeleteConfirm
              title="Apakah Anda yakin ingin menghapus bukti pembayaran dan mengubah status menjadi belum dibayar?"
              okText="Hapus"
              okButtonProps={{ danger: true }}
              cancelText="Batal"
              onConfirm={() =>
                doDelete(
                  {
                    id: buktiBayarId!,
                    resource: `bukti-bayar`,
                    dataProviderName: 'lms',
                    successNotification: {
                      message: 'Bukti pembayaran berhasil dihapus',
                      type: 'success',
                      description: 'Sukses',
                    },
                    errorNotification: (err) =>
                      showErrorNotification(
                        err,
                        'There was a problem when deleting bukti pembayaran',
                      ),
                  },
                  {
                    onSuccess: () => {
                      handleClose()
                      onSuccess(userId)
                    },
                  },
                )
              }
            >
              <Button danger>Hapus</Button>
            </PopDeleteConfirm>
          )}

          {!imgUrl && !isEditing && (
            <Button
              type="primary"
              onClick={async () => {
                form.setFieldsValue({
                  paymentProof: imgUrl,
                  paymentDate: date ? dayjs(date) : undefined,
                  amount,
                  notes,
                })
                setEditing(true)
              }}
            >
              Tambah Bukti Pembayaran
            </Button>
          )}

          {isEditing && (
            <Button type="primary" onClick={handleSave}>
              Simpan
            </Button>
          )}
        </Space>
      }
    >
      <Form form={form}>
        <Descriptions bordered column={1}>
          <Descriptions.Item label="Nama Fasilitator">{name}</Descriptions.Item>
          <Descriptions.Item
            labelStyle={{ verticalAlign: 'top' }}
            label={
              <>
                Tanggal Pembayaran
                {isEditing && <TextField type="danger" value="*" />}
              </>
            }
          >
            {isEditing ? (
              <Form.Item
                name="paymentDate"
                required
                rules={[
                  {
                    required: true,
                    message: 'Tanggal harus diisi',
                  },
                ]}
                style={{ marginBottom: 0 }}
              >
                <DatePicker
                  locale={locale.DatePicker}
                  format={'DD MMM YYYY'}
                  showToday={false}
                  style={{ width: 160 }}
                />
              </Form.Item>
            ) : (
              date || '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item
            labelStyle={{ verticalAlign: 'top' }}
            label={
              <>
                Jumlah Pembayaran
                {isEditing && <TextField type="danger" value="*" />}
              </>
            }
          >
            {isEditing ? (
              <Form.Item
                name="amount"
                required
                rules={[
                  { required: true, message: 'Jumlah harus diisi' },
                  {
                    validator(_, value) {
                      if (+value <= 0) {
                        return Promise.reject(
                          'Tidak boleh kurang atau sama dengan 0',
                        )
                      }

                      return Promise.resolve()
                    },
                  },
                ]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber
                  prefix="Rp"
                  controls={false}
                  stringMode
                  style={{ width: 160 }}
                  formatter={separateThousand}
                  parser={(value) => value!.replace(/(\.*)/g, '')}
                />
              </Form.Item>
            ) : amount ? (
              `Rp${separateThousand(amount)}`
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item
            labelStyle={{ verticalAlign: 'top' }}
            label="Catatan"
          >
            {isEditing ? (
              <Form.Item name="notes" style={{ marginBottom: 0 }}>
                <Input.TextArea rows={2} style={{ resize: 'none' }} />
              </Form.Item>
            ) : (
              notes || '-'
            )}
          </Descriptions.Item>
        </Descriptions>
        <div className="my-4 text-center">
          {imgUrl ? (
            <Image
              alt="payment proof"
              height={400}
              wrapperClassName="mx-auto !block"
              className="object-contain"
              src={imgUrl}
            />
          ) : preview ? null : (
            <Empty description="Tidak ada bukti pembayaran" />
          )}
          {preview && (
            <Image
              alt="payment proof"
              height={400}
              wrapperClassName="mx-auto !block"
              className="object-contain"
              src={preview}
            />
          )}
          {isEditing && (
            <Form.Item
              name="paymentProof"
              rules={[{ required: true, message: 'Gambar harus diisi' }]}
            >
              <Upload
                accept=".jpg, .jpeg, .png"
                beforeUpload={beforeUpload}
                multiple={false}
                showUploadList={false}
              >
                <Button className="mt-4">
                  {preview ? 'Ganti' : 'Unggah'} Gambar
                </Button>
              </Upload>
            </Form.Item>
          )}
        </div>
      </Form>
    </Modal>
  )
}

export default UploadPaymentProofModal
